import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type TextFieldEvent } from '@volvo/vcdk';
import { Button, Checkbox, Modal, TextField, Typography } from '@volvo/vcdk-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import type { IUnit } from '@/service/vpc/types/IUnit';
import type { ColumnDef } from '@tanstack/react-table';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import ProductDesignation from '@/components/ProductDesignation';
import Table from '@/components/Table';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { queryKeys } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';
import { chassis, unitLocation } from '@/utils';

type Props = {
  buttonText?: string;
  operation: 'new' | 'update';
  preSelectedUnitIds?: string[];
  preSelectedFleetId?: string;
  preExistingUnitIds?: string[];
  onSuccess?: () => void;
};

const ManageUnitsFleetModal: React.FC<Props> = ({
  buttonText,
  operation,
  preSelectedUnitIds = [],
  preSelectedFleetId,
  preExistingUnitIds = [],
  onSuccess,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isLoading, fleetAccountId, fleetAccountUnits } = useFleetAccount();

  const [tableKey, setTableKey] = useState<string>(uuidv4());
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState<boolean>(false);

  const [selectedFleetName, setSelectedFleetName] = useState<string>('');
  const [selectedUnitsIds, setSelectedUnitsIds] = useState<string[]>([]);

  const { mutateAsync: createFleet, isPending } = useMutation({
    mutationFn: (props: { fleetAccountId: string; name: string }) => vpcService.Fleet.createFleet(props),
    // The service retries itself; a mutation retry would create a second fleet with a new id
    retry: false,
  });

  const { mutateAsync: addUnitsToFleet, isPending: isAddingUnits } = useMutation({
    mutationFn: (props: { fleetId: string; endCustomerUnitIds: string[] }) => vpcService.Fleet.addUnitsToFleet(props),
    retry: false,
  });

  const handleOnChange = (e: TextFieldEvent) => {
    setSelectedFleetName(e.detail.value);
  };

  const availableUnits =
    fleetAccountUnits?.filter((unit) => !preExistingUnitIds.includes(String(unit.endCustomerUnitId))) || [];

  const availablePreSelectedUnitIds = preSelectedUnitIds.filter((id) =>
    availableUnits.some((unit) => String(unit.endCustomerUnitId) === id)
  );

  // Row selection is keyed by row index in availableUnits
  const preSelectedRows = Object.fromEntries(
    availableUnits.flatMap((unit, index) =>
      availablePreSelectedUnitIds.includes(String(unit.endCustomerUnitId)) ? [[index, true]] : []
    )
  );

  const openModal = () => {
    setSelectedUnitsIds(availablePreSelectedUnitIds);
    setSelectedFleetName('');
    setIsOpen(true);
  };

  const resetModal = () => {
    setSelectedUnitsIds([]);
    setSelectedFleetName('');
    setTableKey(uuidv4());
    setIsOpen(false);
  };

  const handleOnSubmit = async () => {
    if (!fleetAccountId) return;
    let fleetId = preSelectedFleetId ?? '';

    if (operation === 'new') {
      const response = await createFleet({ fleetAccountId: fleetAccountId, name: selectedFleetName });
      fleetId = response.fleetId;
    }
    if (selectedUnitsIds.length > 0) {
      await addUnitsToFleet({
        fleetId: fleetId,
        endCustomerUnitIds: selectedUnitsIds.map((id) => id),
      });
    }

    await queryClient.invalidateQueries({ queryKey: [queryKeys.getFleets], exact: false });
    await queryClient.invalidateQueries({ queryKey: [queryKeys.getFleetUnitsCount], exact: false });
    await queryClient.invalidateQueries({ queryKey: [queryKeys.getFleetUnits], exact: false });

    onSuccess?.();
    setIsSuccessOpen(true);
    resetModal();
  };

  const isDisabled =
    isPending ||
    isAddingUnits ||
    (operation === 'new' ? !selectedFleetName.trim() : !preSelectedFleetId || selectedUnitsIds.length === 0);

  const columns = [
    {
      accessorKey: 'select',
      id: 'select',
      header: ({ table }) => {
        const selectableRows = table.getRowModel();
        const allSelectableSelected = selectableRows.rows.every((row) => row.getIsSelected());
        const someSelectableSelected = selectableRows.rows.some((row) => row.getIsSelected());

        return (
          <Checkbox
            checked={allSelectableSelected}
            indeterminate={someSelectableSelected && !allSelectableSelected}
            onChange={() => {
              const rowUnitIds = selectableRows.rows.map((row) => String(row.original.endCustomerUnitId));

              selectableRows.rows.forEach((row) => {
                row.toggleSelected(!allSelectableSelected);
              });

              setSelectedUnitsIds((prevSelectedUnitsIds) => {
                if (!allSelectableSelected) {
                  // Add all row unit IDs that aren't already selected
                  const newIds = rowUnitIds.filter((id) => !prevSelectedUnitsIds.includes(id));
                  return [...prevSelectedUnitsIds, ...newIds];
                } else {
                  // Remove all row unit IDs
                  return prevSelectedUnitsIds.filter((id) => !rowUnitIds.includes(id));
                }
              });
            }}
          />
        );
      },
      size: 40,
      meta: {
        order: 4,
      },
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onChange={(e) => {
            const isChecked = e.detail.checked;
            row.toggleSelected(isChecked);
            setSelectedUnitsIds((prevSelectedUnitsIds) => {
              if (isChecked) {
                // Now checked - add it
                return [...prevSelectedUnitsIds, String(row.original.endCustomerUnitId)];
              } else {
                // Now unchecked - remove it
                return prevSelectedUnitsIds.filter((unit) => unit !== String(row.original.endCustomerUnitId));
              }
            });
          }}
        />
      ),
    },
    {
      accessorFn: (row) => row.unitName,
      id: 'unitName',
      header: t(($) => $.components.table.unit_table.unit_name),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-col">
            <Typography variant="caption1" className="text-subtle truncate">
              {row.metadata?.unitName || info.getValue<string>()}
            </Typography>
            <Typography variant="caption2" className="text-subtle truncate">
              {unitLocation.getUnitLocation(row)}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => chassis.getUnitSerialNumbers(row),
      id: 'engines',
      header: t(($) => $.components.table.unit_table.engines),
      size: 140,
      maxSize: 140,
      cell: (info) => {
        const serienumbers = info.getValue<string[]>();
        return <ProductDesignation serialNumbers={serienumbers} />;
      },
    },
  ] as ColumnDef<IUnit>[];

  return (
    <>
      <Button variant="primary" size="medium" onClick={openModal}>
        {buttonText ??
          (operation === 'new'
            ? t(($) => $.components.modal.add_units_fleet_modal.button_add)
            : t(($) => $.components.modal.add_units_fleet_modal.button_update))}
      </Button>
      {isOpen &&
        createPortal(
          <Modal open={isOpen} onClose={() => resetModal()} width={700} maxWidth="90%">
            {operation === 'new' ? (
              <span slot="header">{t(($) => $.components.modal.add_units_fleet_modal.title_add)}</span>
            ) : (
              <span slot="header">{t(($) => $.components.modal.add_units_fleet_modal.title_update)}</span>
            )}
            <div className="mb-6 flex flex-col gap-6">
              {operation === 'new' ? (
                <>
                  <TextField
                    label={t(($) => $.components.modal.add_units_fleet_modal.fleet_name)}
                    value={selectedFleetName}
                    size="medium"
                    className="[&::part(input-wrapper)]:h-10"
                    onChange={handleOnChange}
                  />
                </>
              ) : (
                <></>
              )}

              <Table
                key={tableKey}
                isLoading={isLoading}
                headerTitle="Units"
                columns={columns}
                data={availableUnits || []}
                initialRowSelection={preSelectedRows}
                disableMobileView={true}
              />
            </div>
            <span slot="footer">
              <div className="flex gap-4">
                <Button
                  variant="secondary"
                  size="medium"
                  disabled={isPending || isAddingUnits}
                  onClick={() => resetModal()}
                >
                  {t(($) => $.components.modal.add_units_fleet_modal.cancel)}
                </Button>
                <Button
                  variant="primary"
                  size="medium"
                  loading={isPending || isAddingUnits}
                  disabled={isDisabled}
                  onClick={() => handleOnSubmit()}
                >
                  {selectedUnitsIds.length > 0 && operation === 'update'
                    ? t(($) => $.components.modal.add_units_fleet_modal.submit.add_units)
                    : t(($) => $.components.modal.add_units_fleet_modal.submit.create_fleet)}
                </Button>
              </div>
            </span>
          </Modal>,
          document.body
        )}
      {isSuccessOpen &&
        createPortal(
          <Modal open={isSuccessOpen} onClose={() => setIsSuccessOpen(false)} width={500} maxWidth="90%">
            <span slot="header">{t(($) => $.components.modal.add_units_fleet_modal.title_success)}</span>
            <div className="mb-6 flex flex-col gap-6">
              <p>{t(($) => $.components.modal.add_units_fleet_modal.text_success)}</p>
            </div>
            <span slot="footer">
              <div className="flex gap-4">
                <Button
                  variant="primary"
                  size="medium"

                  onClick={() => setIsSuccessOpen(false)}
                >
                  {t(($) => $.components.modal.add_units_fleet_modal.submit.close)}
                </Button>
              </div>
            </span>
          </Modal>,
          document.body
        )}
    </>
  );
};

export default withComponentErrorBoundary(ManageUnitsFleetModal);
